import React, {useMemo, useState} from 'react';
import {useQuery} from "@apollo/client";
import _ from "lodash";
import {Box, SxProps} from "@mui/material";
import {
    InstalledBaseNode,
    InstalledBaseNodeTypeCategory,
    InstalledBaseNodeTypeKey
} from "../../codegen/graphql";
import {InstalledBaseNode as InstalledBaseNodeQuery} from '../../graphql/queries/installedBaseNode';
import {InstalledBaseNodeListTdas} from '../../graphql/queries/installedBaseNodeListTdas';
import InstalledBaseTreeColumnHeader from "./header";
import InstalledBaseTreeList from "./list";
import InstalledBaseFilter from "./filter";
import CreateNodeModal from "./CreateNodeModal";
import {MoveInstalledBaseNodeSuccessInfo} from "../MoveInstalledBaseNodeModal";
import {useTranslation} from "react-i18next";
import {TypeDefinitionAttribute} from "../../types";
import {GuidanceAction} from "../../utils/emptyColumnGuidance";
import "./index.css";


interface Props {
    parentId: string | null;
    columnIndex: number;
    onChangeNode: (id: string, options?: { viaKeyboard?: boolean }) => void;
    focusFirstItemTrigger?: boolean;
    onFirstItemFocused?: () => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    filter: {[key: string]: any};
    onFilterChange: (filter: {[key: string]: any}) => void;
    allSelectedNodeIds?: (string | null)[];
    selectedNodeId?: string | null;
    createNodeCallback?: (nodeId: string) => void;
    deleteNodeCallback?: (deletedNodeId: string) => void;
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
    onOpenNodeDetail?: (nodeId: string) => void;
    // Edit/Details action of the row and header menus: opens the detail panel
    // on Information for this column's node or one of its rows.
    onEditNodeDetail?: (nodeId: string) => void;
    // Node whose detail panel is currently open (its row button acts as a close toggle).
    detailNodeId?: string | null;
    onNoChildren?: (index: number) => void;
    autoSkipRoot?: (nodeId: string) => void;
    onRootLoaded?: () => void;
    onRootEmpty?: (isEmpty: boolean) => void;
    sx?: SxProps;
    // false while the column is hidden via display:none (root column during
    // the initial root check). The list uses this to skip scrollIntoView
    // while hidden and re-fire it once the column is rendered.
    visible?: boolean;
}


const InstalledBaseTreeColumn = ({
                                     parentId,
                                     columnIndex,
                                     onChangeNode,
                                     focusFirstItemTrigger,
                                     onFirstItemFocused,
                                     searchQuery,
                                     onSearchChange,
                                     filter,
                                     onFilterChange,
                                     allSelectedNodeIds,
                                     selectedNodeId,
                                     createNodeCallback,
                                     deleteNodeCallback,
                                     moveNodeCallback,
                                     onOpenNodeDetail,
                                     onEditNodeDetail,
                                     detailNodeId,
                                     onNoChildren,
                                     autoSkipRoot,
                                     onRootLoaded,
                                     onRootEmpty,
                                     sx,
                                     visible = true,
}: Props) => {
    const {t} = useTranslation();

    const [isFilterOpen, setFilterOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    // How the create modal was opened. `undefined` (or a `select` action) means "let the
    // user pick the type"; a `type`/`asset` action from the empty-state guidance skips the
    // selection screen. Reset on close so the next open starts clean.
    const [createIntent, setCreateIntent] = useState<GuidanceAction | undefined>(undefined);

    const openCreateModal = (action?: GuidanceAction) => {
        setCreateIntent(action);
        setCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setCreateModalOpen(false);
        setCreateIntent(undefined);
    };

    const tdasOptions = useMemo(
        () => (parentId !== null ? {parentNodeId: parentId} : {}),
        [parentId]
    );

    const {data: tdasData} = useQuery(InstalledBaseNodeListTdas, {
        variables: {
            typeDefinitionIndex: "INSTALLED_BASE_NODE_LIST_FILTER",
            options: tdasOptions
        }
    });

    const filterAttributes: TypeDefinitionAttribute[] = useMemo(
        () => _.get(tdasData, 'installedBaseNodeList.typeDefinitionAttributeList.typeDefinitionAttributes', []),
        [tdasData]
    );

    const {
        data: parentData,
        loading: parentLoading,
        refetch: parentRefetch
    } = useQuery(InstalledBaseNodeQuery, {
        variables: {
            id: parentId
        },
        skip: !parentId
    })

    let parentNode: InstalledBaseNode = _.get(parentData, 'installedBaseNode');

    if (parentId === null) {
        parentNode = {
            assetCount: 0,
            childrenNodeCount:0,
            canDocumentNetworkScan: false,
            insert: undefined,
            update: undefined,
            isStarred: false,
            key: 'ClientList',
            id: 'clientList',
            title: t('Companies'),
            availableInstalledBaseEvents: [],
            nodeType: {
                id: 'client',
                key: InstalledBaseNodeTypeKey.CLIENT,
                name: t('Company'),
                category: InstalledBaseNodeTypeCategory.general,
                description: '',
                recommended: false
            }
        }
    }

    const handleRefetch = () => {
        parentRefetch();
    };

    const handleFilterChange = (values: { [key: string]: any }) => {
        // Nur Werte setzen, die nicht null/undefined sind.
        const nonNullValues = Object.fromEntries(
            Object.entries(values).filter(([_, value]) => value != null)
        );
        if (Object.keys(nonNullValues).length > 0) {
            onFilterChange(nonNullValues);
        }
        setFilterOpen(false)
    };

    const resetFilterValues = () => {
        onFilterChange({});
        setFilterOpen(false)
    }

    return (
        <>
            <Box
                className={"installed-base-column"}
                sx={{
                    minWidth: "360px",
                    maxWidth: "360px",
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    ...sx
                }}
            >
                <InstalledBaseTreeColumnHeader
                    parentId={parentId}
                    parentNode={parentNode}
                    searchQuery={searchQuery}
                    onSearch={onSearchChange}
                    refetchFunction={() => {
                        handleRefetch();
                    }}
                    isLoading={parentLoading}
                    deleteNodeCallback={deleteNodeCallback}
                    moveNodeCallback={moveNodeCallback}
                    onEditNodeDetail={onEditNodeDetail}
                    isFilterOpen={isFilterOpen}
                    activeFilterCount={Object.keys(filter).length}
                    onShowFilterContent={(isOpen: boolean) => setFilterOpen(isOpen)}
                    onOpenCreateModal={() => openCreateModal()}
                />

                {
                    isFilterOpen ?
                        <InstalledBaseFilter
                            filter={filter}
                            attributes={filterAttributes}
                            onSubmit={handleFilterChange}
                            onResetValues={resetFilterValues}
                        />
                        :
                        <InstalledBaseTreeList
                            parentId={parentId}
                            parentNode={parentNode}
                            parentLoading={parentLoading}
                            searchQuery={searchQuery}
                            allSelectedNodeIds={allSelectedNodeIds}
                            selectedNodeId={selectedNodeId}
                            onChangeNode={onChangeNode}
                            focusFirstItemTrigger={focusFirstItemTrigger}
                            onFirstItemFocused={onFirstItemFocused}
                            filter={filter}
                            filterAttributes={filterAttributes}
                            columnIndex={columnIndex}
                            onNoChildren={onNoChildren}
                            onAutoSkipRoot={nodeId => {
                                if (parentId === null) {
                                    autoSkipRoot?.(nodeId);
                                }
                            }}
                            onRootLoaded={onRootLoaded}
                            onRootEmpty={isEmpty => {
                                if (parentId === null) {
                                    onRootEmpty?.(isEmpty);
                                }
                            }}
                            onOpenCreateModal={openCreateModal}
                            onClearSearch={() => onSearchChange('')}
                            onClearFilter={() => onFilterChange({})}
                            refetchFunction={handleRefetch}
                            deleteNodeCallback={deleteNodeCallback}
                            moveNodeCallback={moveNodeCallback}
                            onOpenNodeDetail={onOpenNodeDetail}
                            onEditNodeDetail={onEditNodeDetail}
                            detailNodeId={detailNodeId}
                            visible={visible}
                        />
                }
            </Box>
            {createModalOpen && <CreateNodeModal
                open={createModalOpen}
                onClose={closeCreateModal}
                node={parentNode}
                createNodeCallback={createNodeCallback}
                refetchFunction={handleRefetch}
                initialNodeType={createIntent?.kind === 'type' || createIntent?.kind === 'asset'
                    ? createIntent.type
                    : undefined}
                // Only an explicit "pick a type" intent suppresses the single-recommendation
                // auto-skip; the header "+" keeps its existing shortcut behaviour.
                forceTypeSelection={createIntent?.kind === 'select'}
            />}
        </>
    )
};

export default InstalledBaseTreeColumn;
